import type {
  ChargeRecord,
  CitizenSummary,
  EvidenceRecord,
  IncidentCharge,
  IncidentOfficer,
  IncidentReference,
  PageResult,
  TagRecord,
  VehicleRecord,
} from "@hydeck/mdt-contracts";
import { createResource, createSignal, For, Show, type JSX } from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

type RecordItem = {
  id: number;
  categoryId?: number;
  criminals?: Array<Omit<CitizenSummary, "charges"> & { charges?: IncidentCharge[] }>;
  citizens?: CitizenSummary[];
  evidence?: EvidenceRecord[];
  officers?: IncidentOfficer[];
  vehicles?: VehicleRecord[];
  tags?: string[];
  references?: IncidentReference[];
  referenced?: IncidentReference[];
  history?: Array<{ id: number; action: string; actor: string; details: string; createdAt: string }>;
};

export function RecordRelations(props: {
  type: "incident" | "report";
  item: RecordItem;
  onReload: () => Promise<void>;
}) {
  const [evidenceId, setEvidenceId] = createSignal("");
  const [citizenId, setCitizenId] = createSignal("");
  const [officerId, setOfficerId] = createSignal("");
  const [vehicleId, setVehicleId] = createSignal("");
  const [tagId, setTagId] = createSignal("");
  const [referenceId, setReferenceId] = createSignal("");
  const [referenceType, setReferenceType] = createSignal<"incident" | "report">("incident");
  const [chargeId, setChargeId] = createSignal("");
  const [chargeVariant, setChargeVariant] = createSignal<"base" | "accessory" | "accomplice">("base");
  const [busy, setBusy] = createSignal(false);
  const token = () => currentUser()?.accessToken;
  const [chargeCatalog] = createResource(
    () => props.type === "incident" ? props.item.id : 0,
    (recordId) => recordId
      ? apiRequest<PageResult<ChargeRecord>>("/charges", { token: token(), query: { page: 1, limit: 500 } })
      : Promise.resolve({ items: [], meta: { page: 1, limit: 500, total: 0, pages: 0 } }),
  );
  const base = () => props.type === "incident"
    ? `/incidents/${props.item.id}`
    : `/reports/${props.item.categoryId}/${props.item.id}`;
  const relation = (name: string) => `${base()}/${name}`;

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      await props.onReload();
    } finally {
      setBusy(false);
    }
  };

  const addEvidence = () => {
    const id = positive(evidenceId());
    if (!id) return;
    void run(() => apiRequest(relation("evidence"), { method: "POST", token: token(), body: { evidenceId: id } }));
    setEvidenceId("");
  };
  const addCitizen = () => {
    const id = positive(citizenId());
    if (!id) return;
    if (props.type === "incident") {
      void run(() => apiRequest(`${base()}/citizens`, { method: "POST", token: token(), body: { citizenId: id } }));
    } else {
      const values = [...(props.item.citizens ?? []).map((item) => ({ citizenId: item.id })), { citizenId: id }];
      void run(() => apiRequest(relation("citizens-involved"), { method: "PATCH", token: token(), body: { citizensInvolved: values } }));
    }
    setCitizenId("");
  };
  const addOfficer = () => {
    const id = positive(officerId());
    if (!id) return;
    const values = [...(props.item.officers ?? []).map((item) => ({ userId: item.id })), { userId: id }];
    void run(() => apiRequest(relation("officers-involved"), { method: "PATCH", token: token(), body: { officersInvolved: values } }));
    setOfficerId("");
  };
  const addVehicle = () => {
    const id = positive(vehicleId());
    if (!id) return;
    const values = [...(props.item.vehicles ?? []).map((item) => ({ vehicleId: item.id })), { vehicleId: id }];
    void run(() => apiRequest(relation("vehicles"), { method: "PATCH", token: token(), body: { vehicles: values } }));
    setVehicleId("");
  };
  const updateTags = async (extra: number | null, removeName?: string) => {
    const all = await apiRequest<TagRecord[]>("/tags", { token: token() });
    const ids = all.filter((tag) => (props.item.tags ?? []).includes(tag.name) && tag.name !== removeName).map((tag) => tag.id);
    if (extra && !ids.includes(extra)) ids.push(extra);
    await apiRequest(`${base()}/tags`, { method: "PATCH", token: token(), body: { tags: ids.map((id) => ({ tagId: id })) } });
  };
  const addTag = () => {
    const id = positive(tagId());
    if (!id) return;
    void run(() => updateTags(id));
    setTagId("");
  };
  const addReference = () => {
    const id = positive(referenceId());
    if (!id) return;
    const references = [...(props.item.references ?? []), { referenceType: referenceType(), reference: { id, title: "", reportCategoryId: props.type === "report" ? props.item.categoryId ?? null : null } }];
    void run(() => apiRequest(relation("references"), { method: "PATCH", token: token(), body: { references } }));
    setReferenceId("");
  };
  const removeCriminal = (citizenIdValue: number) => {
    if (props.type === "incident") void run(() => apiRequest(`${base()}/citizens/${citizenIdValue}`, { method: "DELETE", token: token() }));
  };
  const toggleCriminalState = (citizen: CitizenSummary, key: "isWarrant" | "isGuilty" | "isProcessed") => {
    if (props.type === "incident") void run(() => apiRequest(`${base()}/citizens/${citizen.id}`, { method: "PATCH", token: token(), body: { [key]: !citizen[key] } }));
  };
  const saveCriminalState = (citizen: CitizenSummary, event: Event) => {
    if (props.type !== "incident") return;
    const expiry = (event.currentTarget as HTMLInputElement).value;
    void run(() => apiRequest(`${base()}/citizens/${citizen.id}`, {
      method: "PATCH",
      token: token(),
      body: { warrantExpiry: expiry ? new Date(expiry).toISOString() : null, isWarrant: citizen.isWarrant ?? false },
    }));
  };
  const toggleApproval = (citizen: CitizenSummary) => {
    if (props.type !== "incident" || !hasPermission("incidents.approve")) return;
    void run(() => apiRequest(`${base()}/citizens/${citizen.id}`, {
      method: "PATCH",
      token: token(),
      body: { approvedBy: citizen.approvedBy ? null : currentUser()?.id ?? null },
    }));
  };
  const exportCriminal = async (citizen: CitizenSummary) => {
    if (props.type !== "incident" || !hasPermission("incidents.export")) return;
    setBusy(true);
    try {
      const url = await apiRequest<string>(`${base()}/citizens/${citizen.id}/export`, {
        method: "POST",
        token: token(),
      });
      await navigator.clipboard.writeText(url);
    } finally {
      setBusy(false);
    }
  };
  const addCharge = (citizen: CitizenSummary & { charges?: IncidentCharge[] }) => {
    const id = positive(chargeId());
    if (props.type !== "incident" || !id) return;
    const source = chargeCatalog()?.items.find((charge) => charge.id === id);
    if (!source) return;
    const variant = chargeVariant();
    const isAccessory = variant === "accessory";
    const isAccomplice = variant === "accomplice";
    const charges: IncidentCharge[] = [...(citizen.charges ?? []), {
      chargeId: id,
      name: source.name,
      type: source.type,
      count: 1,
      isAccessory,
      isAccomplice,
      time: isAccessory ? source.accessoryTime ?? source.time ?? source.jailTime : isAccomplice ? source.accompliceTime ?? source.time ?? source.jailTime : source.time ?? source.jailTime,
      fine: isAccessory ? source.accessoryFine ?? source.fine : isAccomplice ? source.accompliceFine ?? source.fine : source.fine,
      points: source.points,
      accessoryTime: source.accessoryTime,
      accessoryFine: source.accessoryFine,
      accompliceTime: source.accompliceTime,
      accompliceFine: source.accompliceFine,
    }];
    void run(() => apiRequest(`${base()}/citizens/${citizen.id}/charges`, { method: "PATCH", token: token(), body: { charges } }));
    setChargeId("");
  };
  const removeCharge = (citizen: CitizenSummary & { charges?: IncidentCharge[] }, charge: IncidentCharge) => {
    if (props.type !== "incident") return;
    const charges = (citizen.charges ?? []).filter((item) => !(item.chargeId === charge.chargeId && item.isAccessory === charge.isAccessory && item.isAccomplice === charge.isAccomplice));
    void run(() => apiRequest(`${base()}/citizens/${citizen.id}/charges`, { method: "PATCH", token: token(), body: { charges } }));
  };

  return (
    <div class="flex flex-col gap-3 pt-2">
      <RelationSection title="Criminals">
        <Show when={props.item.criminals?.length} fallback={<p class="p-2 text-sm text-white-55">No criminals attached.</p>}>
          <For each={props.item.criminals}>
            {(citizen) => (
              <div class="flex flex-col gap-1 border-l-2 border-red-400 bg-white-11 p-2">
                <div class="flex justify-between gap-2"><span>#{citizen.id} {citizen.name}</span><span>{citizen.isWarrant || citizen.isWanted ? "WARRANT" : ""}</span></div>
                <div class="flex flex-wrap gap-1 text-xs">
                  <Show when={props.type === "incident"}>
                    <button class={`px-2 py-1 ${citizen.isWarrant ? "bg-red-gradient" : "bg-gray-gradient"}`} onClick={() => toggleCriminalState(citizen, "isWarrant")}>Warrant</button>
                    <button class={`px-2 py-1 ${citizen.isGuilty ? "bg-red-gradient" : "bg-gray-gradient"}`} onClick={() => toggleCriminalState(citizen, "isGuilty")}>Guilty</button>
                    <button class={`px-2 py-1 ${citizen.isProcessed ? "bg-green-gradient" : "bg-gray-gradient"}`} onClick={() => toggleCriminalState(citizen, "isProcessed")}>Processed</button>
                    <Show when={hasPermission("incidents.approve")}>
                      <button class={`px-2 py-1 ${citizen.approvedBy ? "bg-green-gradient" : "bg-gray-gradient"}`} onClick={() => toggleApproval(citizen)}>{citizen.approvedBy ? "Approved" : "Approve"}</button>
                    </Show>
                    <Show when={hasPermission("incidents.export")}>
                      <button class="bg-blue-gradient px-2 py-1" disabled={busy()} onClick={() => void exportCriminal(citizen)}>Export</button>
                    </Show>
                    <button class="bg-red-gradient px-2 py-1" onClick={() => removeCriminal(citizen.id)}>Remove</button>
                    <label class="flex items-center gap-1 bg-white-11 px-2 py-1 text-xs">
                      Warrant expiry
                      <input type="datetime-local" class="bg-transparent text-xs" value={citizen.warrantExpiry ? new Date(citizen.warrantExpiry).toISOString().slice(0, 16) : ""} onChange={(event) => saveCriminalState(citizen, event)} />
                    </label>
                  </Show>
                </div>
                <Show when={citizen.charges?.length}>
                  <div class="flex flex-col gap-1 p-1">
                    <For each={citizen.charges}>{(charge) => <div class="flex items-center justify-between gap-2 text-xs text-white-55">
                      <span>{charge.count}x {charge.name || `Charge #${charge.chargeId}`} {charge.isAccessory ? "(Accessory)" : charge.isAccomplice ? "(Accomplice)" : ""} - ${charge.fine} / {charge.time}m / {charge.points} pts</span>
                      <button class="text-red-300" disabled={busy()} onClick={() => removeCharge(citizen, charge)}>x</button>
                    </div>}</For>
                    <p class="text-xs text-cyan">Total: {totalTime(citizen.charges ?? [])}m · ${totalFine(citizen.charges ?? [])} · {totalPoints(citizen.charges ?? [])} pts</p>
                  </div>
                </Show>
                <Show when={props.type === "incident"}>
                  <div class="flex flex-wrap gap-2 p-2">
                    <select class="min-h-8 min-w-[18rem] flex-1 bg-white-11 px-2 text-xs" value={chargeId()} onChange={(event) => setChargeId(event.currentTarget.value)}>
                      <option value="">Select charge...</option>
                      <For each={chargeCatalog()?.items ?? []}>{(charge) => <option value={charge.id}>{charge.name} · {charge.time ?? charge.jailTime}m · ${charge.fine}</option>}</For>
                    </select>
                    <select class="min-h-8 bg-white-11 px-2 text-xs" value={chargeVariant()} onChange={(event) => setChargeVariant(event.currentTarget.value as "base" | "accessory" | "accomplice")}>
                      <option value="base">Base</option>
                      <option value="accessory">Accessory</option>
                      <option value="accomplice">Accomplice</option>
                    </select>
                    <button class="bg-green-gradient px-3 text-xs" disabled={busy() || !chargeId()} onClick={() => addCharge(citizen)}>Add charge</button>
                  </div>
                </Show>
              </div>
            )}
          </For>
        </Show>
        <Show when={props.type === "incident"}><InlineAdd value={citizenId()} setValue={setCitizenId} placeholder="Citizen state ID" onAdd={addCitizen} disabled={busy()} /></Show>
      </RelationSection>

      <RelationSection title="Tags">
        <div class="flex flex-wrap gap-2 p-2"><For each={props.item.tags ?? []}>{(tag) => <span class="bg-white-11 px-2 py-1 text-sm">{tag}</span>}</For></div>
        <InlineAdd value={tagId()} setValue={setTagId} placeholder="Tag ID" onAdd={addTag} disabled={busy()} />
      </RelationSection>

      <RelationSection title="Evidence">
        <For each={props.item.evidence ?? []}>{(item) => <div class="flex justify-between bg-white-11 p-2 text-sm"><span>{item.type}: {item.description || item.identifier}</span><button class="text-red-300" disabled={busy()} onClick={() => void run(() => apiRequest(`${relation("evidence")}/${item.id}`, { method: "DELETE", token: token() }))}>x</button></div>}</For>
        <InlineAdd value={evidenceId()} setValue={setEvidenceId} placeholder="Evidence ID" onAdd={addEvidence} disabled={busy()} />
      </RelationSection>

      <RelationSection title="Officers Involved">
        <For each={props.item.officers ?? []}>{(item) => <span class="mr-2 inline-block bg-white-11 px-2 py-1 text-sm">#{item.callsign} {item.firstName} {item.lastName}</span>}</For>
        <InlineAdd value={officerId()} setValue={setOfficerId} placeholder="User ID" onAdd={addOfficer} disabled={busy()} />
      </RelationSection>
      <RelationSection title="Citizens Involved">
        <For each={props.item.citizens ?? []}>{(item) => <span class="mr-2 inline-block bg-white-11 px-2 py-1 text-sm">#{item.id} {item.name}</span>}</For>
        <Show when={props.type === "report"}><InlineAdd value={citizenId()} setValue={setCitizenId} placeholder="Citizen state ID" onAdd={addCitizen} disabled={busy()} /></Show>
      </RelationSection>
      <RelationSection title="Vehicles">
        <For each={props.item.vehicles ?? []}>{(item) => <span class="mr-2 inline-block bg-white-11 px-2 py-1 text-sm">{item.plate} · {item.model}</span>}</For>
        <InlineAdd value={vehicleId()} setValue={setVehicleId} placeholder="Vehicle ID" onAdd={addVehicle} disabled={busy()} />
      </RelationSection>
      <RelationSection title="References">
        <For each={props.item.references ?? []}>{(item) => <span class="mr-2 inline-block bg-white-11 px-2 py-1 text-sm">{item.referenceType} #{item.reference.id} {item.reference.title}</span>}</For>
        <For each={props.item.referenced ?? []}>{(item) => <span class="mr-2 inline-block bg-blue-900/40 px-2 py-1 text-sm">Referenced by {item.referenceType} #{item.reference.id}</span>}</For>
        <div class="flex gap-2 p-2"><select class="bg-white-11 px-2 text-sm" value={referenceType()} onChange={(event) => setReferenceType(event.currentTarget.value as "incident" | "report")}><option value="incident">Incident</option><option value="report">Report</option></select><input class="min-h-8 flex-1 bg-white-11 px-2 text-sm" placeholder="Record ID" value={referenceId()} onInput={(event) => setReferenceId(event.currentTarget.value)} /><button class="bg-green-gradient px-3 text-sm" disabled={busy()} onClick={addReference}>Add</button></div>
      </RelationSection>
      <RelationSection title="History">
        <For each={props.item.history ?? []}>{(item) => <p class="border-b border-white-11 p-2 text-xs">{new Date(item.createdAt).toLocaleString()} · {item.actor} · {item.action}{item.details ? ` — ${item.details}` : ""}</p>}</For>
      </RelationSection>
    </div>
  );
}

function RelationSection(props: { title: string; children: JSX.Element }) {
  return <section class="flex flex-col bg-gray-gradient"><div class="flex min-h-10 items-center bg-white-11 px-3"><span>{props.title}</span></div><div>{props.children}</div></section>;
}

function InlineAdd(props: { value: string; setValue: (value: string) => void; placeholder: string; onAdd: () => void; disabled?: boolean }) {
  return <div class="flex gap-2 p-2"><input class="min-h-8 flex-1 bg-white-11 px-2 text-sm" placeholder={props.placeholder} value={props.value} onInput={(event) => props.setValue(event.currentTarget.value)} /><button class="bg-green-gradient px-3 text-sm disabled:opacity-50" disabled={props.disabled} onClick={props.onAdd}>Add</button></div>;
}

function positive(input: string): number | null {
  const value = Number(input);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

function totalTime(charges: IncidentCharge[]): number {
  return charges.reduce((total, charge) => total + charge.time * Math.max(1, charge.count), 0);
}

function totalFine(charges: IncidentCharge[]): number {
  return charges.reduce((total, charge) => total + charge.fine * Math.max(1, charge.count), 0);
}

function totalPoints(charges: IncidentCharge[]): number {
  return charges.reduce((total, charge) => total + charge.points * Math.max(1, charge.count), 0);
}
